
// booking-site/src/App.jsx - Complete Customer Booking Flow

import React, { useState, useEffect } from 'react';
import { Search, MapPin, DollarSign, Package, ExternalLink, Check, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const STORAGE_TYPES = [
  { value: '', label: 'All Storage Types' },
  { value: 'Self Storage Unit', label: 'Self Storage Unit' },
  { value: 'Warehouse Storage', label: 'Warehouse Storage' },
  { value: 'Mobile Storage Unit', label: 'Mobile Storage Unit' },
  { value: 'Document Storage', label: 'Document Storage' },
  { value: 'Vehicle/Boat Storage', label: 'Vehicle/Boat Storage' }
];

function App() {
  const [facility, setFacility] = useState(null);
  const [units, setUnits] = useState([]);
  const [storageTypes, setStorageTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [termsLinks, setTermsLinks] = useState([]);
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [successBookingId, setSuccessBookingId] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [deposits, setDeposits] = useState([]);  
  const [depositAcceptances, setDepositAcceptances] = useState({}); 
  const [idVerificationRequired, setIdVerificationRequired] = useState(false);

  const subdomain = window.location.hostname.split('.')[0];

  useEffect(() => {
    // Check for success from Stripe redirect
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const bookingId = urlParams.get('booking_id');

    if (success === 'true' && bookingId) {
      console.log('✅ Payment successful, booking:', successBookingId);
      setBookingSuccess(true);
      setSuccessBookingId(bookingId);
      return;
    }

    const verificationComplete = urlParams.get('verification_complete');
    const verificationBookingId = urlParams.get('booking_id');
    
    if (verificationComplete === 'true' && verificationBookingId) {
      handleVerificationReturn(verificationBookingId);
      return;
    }

    loadFacility();
    loadStorageTypes();
    loadTermsLinks();
    loadGoogleMaps();
    loadDeposits();
    loadIdVerificationSetting();
  }, []);

  useEffect(() => {
    if (selectedType !== undefined) {
      loadUnits();
    }
  }, [selectedType]);

  // Load Google Maps dynamically
  const loadGoogleMaps = () => {
    if (window.google) {
      setGoogleMapsLoaded(true);
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.log('Google Maps API key not configured - using basic address input');
      setGoogleMapsLoaded(false);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('✅ Google Maps loaded');
      setGoogleMapsLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps');
      setGoogleMapsLoaded(false);
    };
    document.head.appendChild(script);
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const validateForm = (formData) => {
    const errors = {};

    if (!formData.customer_name || formData.customer_name.trim().length < 2) {
      errors.customer_name = 'Name must be at least 2 characters';
    }

    if (!formData.customer_email || !validateEmail(formData.customer_email)) {
      errors.customer_email = 'Please enter a valid email address';
    }

    if (!formData.customer_phone || !validatePhone(formData.customer_phone)) {
      errors.customer_phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.customer_address || formData.customer_address.trim().length < 5) {
      errors.customer_address = 'Please enter a valid address';
    }

    // ✅ UPDATED: Validate date is not in the past
    if (!formData.move_in_date) {
      errors.move_in_date = 'Please select a move-in date';
    } else {
      const selectedDate = new Date(formData.move_in_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (selectedDate < today) {
        errors.move_in_date = 'Move-in date cannot be in the past';
      }
    }

    if (!formData.duration_months || formData.duration_months < 1) {
      errors.duration_months = 'Please select a rental duration';
    }

    return errors;
  };

  const loadFacility = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}`);
      const data = await response.json();
      
      if (data.success) {
        setFacility(data);
      } else if (data.facility_id) {
        setFacility(data);
      } else {
        console.error('Invalid facility data:', data);
      }
    } catch (error) {
      console.error('Error loading facility:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStorageTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}/storage-types`);
      const data = await response.json();
      if (data.success) {
        setStorageTypes(data.storage_types);
      }
    } catch (error) {
      console.error('Error loading storage types:', error);
    }
  };

  const loadTermsLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}/terms-links`);
      const data = await response.json();
      if (data.success) {
        setTermsLinks(data.links || []);
      }
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  const loadDeposits = async () => {
   try {
    const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}/deposits`);
    const data = await response.json();
    
    if (data.success && Array.isArray(data.deposits)) {
      setDeposits(data.deposits);
      
      // Initialize acceptance checkboxes
      const initialAcceptances = {};
      data.deposits.forEach((_, idx) => {
        initialAcceptances[idx] = false;
      });
      setDepositAcceptances(initialAcceptances);
    }
   } catch (error) {
    console.error('Error loading deposits:', error);
   }
  };

  const loadIdVerificationSetting = async () => {
   try {
    const subdomain = window.location.hostname.split('.')[0];
    const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}/id-verification`);
    const data = await response.json();
    
    if (data.success) {
      setIdVerificationRequired(data.enabled || false);
      console.log('ID verification required:', data.enabled);
    }
   } catch (error) {
     console.error('Error loading ID verification setting:', error);
     setIdVerificationRequired(false);
   }
  };
  
  const loadUnits = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/public/platform/facility/${subdomain}/units`;
      if (selectedType) {
        url += `?storage_type=${encodeURIComponent(selectedType)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setUnits(data.units);
      }
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnitSelect = (unit) => {
    setSelectedUnit(unit);
    setStep(2);
  };

  const handleDetailsSubmit = (details) => {
    const errors = validateForm(details);
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstErrorField = document.querySelector('.error-field');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setFormErrors({});
    setBookingData(details);
    
    if (termsLinks && termsLinks.length > 0) {
      setStep(3);
    } else {
      createBooking(details, false);
    }
  };

  const handleVerificationReturn = async (bookingId) => {
  console.log('🔄 Processing verification return for booking:', bookingId);
  setLoading(true);
  
  try {
    // Check verification status first
    const statusResponse = await fetch(`${API_URL}/api/platform/booking/verification-status/${bookingId}`);
    const statusData = await statusResponse.json();
    
    console.log('📋 Verification status:', statusData);
    
    if (!statusData.success) {
      alert('Error checking verification status. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      window.location.reload();
      return;
    }
    
    // ✅ VERIFIED - Proceed to payment
    if (statusData.verified) {
      console.log('✅ Verification confirmed, proceeding to payment...');
      
      const response = await fetch(`${API_URL}/api/platform/booking/${bookingId}/proceed-to-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.checkout_url) {
        console.log('✅ Redirecting to payment...');
        window.location.href = data.checkout_url;
      } else {
        alert('Error proceeding to payment: ' + (data.error || 'Unknown error'));
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        window.location.reload();
      }
      return;
    }
    
    // ❌ NOT VERIFIED - Handle different failure scenarios
    console.log('⚠️ Verification not complete. Status:', statusData.status);
    
    setLoading(false);
    
    // Show appropriate message based on status
    let message = '';
    let canRetry = false;
    
    if (statusData.status === 'requires_input') {
      message = 'ID verification was not completed.\n\nPlease make sure to:\n• Upload a clear photo of your ID\n• Take a selfie\n• Wait for verification to complete';
      canRetry = true;
    } else if (statusData.status === 'processing') {
      message = 'Your ID is still being verified.\n\nPlease wait a moment...';
      // Auto-retry after 3 seconds for processing status
      setTimeout(() => {
        handleVerificationReturn(bookingId);
      }, 3000);
      return;
    } else if (statusData.status === 'canceled') {
      message = 'ID verification was canceled.';
      canRetry = true;
    } else {
      message = `ID verification incomplete.\n\nStatus: ${statusData.status}`;
      canRetry = true;
    }
    
    // Show retry/cancel choice
    if (canRetry) {
      const retry = window.confirm(
        message + '\n\n' +
        'Click OK to retry verification, or Cancel to start over.'
      );
      
      if (retry) {
        // Retry verification
        console.log('🔄 User chose to retry verification');
        await retryVerification(bookingId);
      } else {
        // Cancel and go back to home
        console.log('❌ User chose to cancel');
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
      }
    } else {
      alert(message);
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
    }
    
   }  catch (error) {
     console.error('❌ Error:', error);
     alert('Error processing verification. The page will reload.');
     window.history.replaceState({}, document.title, window.location.pathname);
     setLoading(false);
     window.location.reload();
   }
  };

  const retryVerification = async (bookingId) => {
  console.log('🔄 Retrying verification for booking:', bookingId);
  setLoading(true);
  
  try {
    // Create a new verification session
    const response = await fetch(`${API_URL}/api/platform/booking/create-verification-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ booking_id: bookingId })
    });
    
    const data = await response.json();
    
    if (data.success && data.verification_url) {
      console.log('✅ New verification session created, redirecting...');
      // Redirect to Stripe Identity again
      window.location.href = data.verification_url;
    } else {
      alert('Error creating verification session: ' + (data.error || 'Unknown error'));
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      window.location.reload();
    }
   }  catch (error) {
      console.error('❌ Error retrying verification:', error);
      alert('Error starting verification. The page will reload.');
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      window.location.reload();
   }
  };
  
  const handleTermsAccept = async () => {
  if (!termsAccepted) {
    alert('Please accept the terms and conditions');
    return;
  }
  
  const allDepositsAccepted = deposits.length === 0 || 
    deposits.every((_, idx) => depositAcceptances[idx]);
  
  if (!allDepositsAccepted) {
    alert('Please accept all deposit requirements');
    return;
  }
  
  setLoading(true);
  
  try {
    // Step 1: Create booking
    const response = await fetch(`${API_URL}/api/platform/booking/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        facility_id: facility.facility_id,
        unit_id: selectedUnit.unit_id,
        customer_name: bookingData.customer_name,
        customer_email: bookingData.customer_email,
        customer_phone: bookingData.customer_phone,
        customer_address: bookingData.customer_address,
        storage_type: selectedUnit.storage_type,
        space_needed: bookingData.space_needed,
        duration_months: bookingData.duration_months,
        move_in_date: bookingData.move_in_date,
        additional_info: bookingData.additional_info,
        terms_accepted: true
      })
    });

    const data = await response.json();

    if (!data.success) {
      alert('Error creating booking: ' + (data.error || 'Unknown error'));
      setLoading(false);
      return;
    }

    // ✅ OPTION 1: No verification needed - checkout_url is returned
    if (data.checkout_url) {
      console.log('✅ No ID verification required, going directly to payment');
      window.location.href = data.checkout_url;
      return;
    }

    // ✅ OPTION 2: Verification needed - needs_verification flag is returned
    if (data.needs_verification && data.booking_id) {
      console.log('✅ ID verification required, starting verification flow');
      
      const bookingId = data.booking_id;
      
      // Create verification session
      const verifyResponse = await fetch(`${API_URL}/api/platform/booking/create-verification-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ booking_id: bookingId })
      });
      
      const verifyData = await verifyResponse.json();
      
      if (verifyData.success && verifyData.verification_url) {
        console.log('✅ Redirecting to Stripe Identity...');
        window.location.href = verifyData.verification_url;
        return;
      } else {
        alert('Error starting ID verification: ' + (verifyData.error || 'Unknown error'));
        setLoading(false);
        return;
      }
    }

    // ❌ Unexpected response
    alert('Unexpected response from server. Please try again.');
    setLoading(false);
    
  } catch (error) {
    console.error('Booking error:', error);
    alert('Failed to create booking. Please try again.');
    setLoading(false);
  }
};

  const createBooking = async (details, termsAcceptedValue) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/platform/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          facility_id: facility.facility_id,
          unit_id: selectedUnit.unit_id,
          customer_name: details.customer_name,
          customer_email: details.customer_email,
          customer_phone: details.customer_phone || '',
          customer_address: details.customer_address || '',
          duration_months: parseInt(details.duration_months),
          move_in_date: details.move_in_date || '',
          additional_info: details.additional_info || '',
          terms_accepted: termsAcceptedValue,
          accepted_deposits: deposits.map((d, idx) => ({  
            type: d.type,
            amount: d.amount,
            accepted: depositAcceptances[idx]
          }))
        })
      });

      const data = await response.json();
      if (data.success) {
        window.location.href = data.checkout_url;
      } else {
        alert(`Error: ${data.error}`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Error creating booking. Please try again.');
      setLoading(false);
    }
  };

  // Success screen
  if (bookingSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '48px',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#10B981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="40" height="40" fill="white" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>

          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#1F2937'
          }}>
            Booking Confirmed!
          </h1>

          <p style={{
            fontSize: '18px',
            color: '#6B7280',
            marginBottom: '24px'
          }}>
            Your storage unit has been successfully reserved.
          </p>

          <div style={{
            background: '#F3F4F6',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '32px'
          }}>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
              Booking Reference
            </p>
            <p style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1F2937',
              fontFamily: 'monospace'
            }}>
              {successBookingId}
            </p>
          </div>

          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            marginBottom: '32px'
          }}>
            A confirmation email has been sent to your email address with all the booking details.
          </p>

          <button
            onClick={() => window.location.href = window.location.origin}
            style={{
              padding: '14px 32px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!facility) {
    return <LoadingScreen />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      <Header facility={facility} />

      <div style={{ maxWidth: '1200px', margin: '32px auto', padding: '0 24px' }}>
        {step === 1 && (
          <SelectUnitStep
            facility={facility}
            units={units}
            storageTypes={storageTypes}
            loading={loading}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            onUnitSelect={handleUnitSelect}
          />
        )}

        {step === 2 && (
          <EnterDetailsStep
            facility={facility}
            selectedUnit={selectedUnit}
            onBack={() => setStep(1)}
            onSubmit={handleDetailsSubmit}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            bookingData={bookingData}
            setBookingData={setBookingData}
            googleMapsLoaded={googleMapsLoaded}
          />
        )}

        {step === 3 && (
          <TermsAcceptanceStep
            facility={facility}
            selectedUnit={selectedUnit}
            bookingData={bookingData}
            termsLinks={termsLinks}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            deposits={deposits}  // ✅ ADD
            depositAcceptances={depositAcceptances}  
            setDepositAcceptances={setDepositAcceptances} 
            idVerificationRequired={idVerificationRequired} 
            loading={loading}
            onBack={() => setStep(2)}
            onAccept={handleTermsAccept}
          />
        )}
      </div>
    </div>
  );
}
// ============================================================================
// COMPONENTS
// ============================================================================

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '18px', fontWeight: '600', color: '#6B7280' }}>Loading...</div>
      </div>
    </div>
  );
}

function Header({ facility }) {
  return (
    <div style={{ background: 'white', borderBottom: '2px solid #E5E7EB', padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '800', color: facility.brand_color, marginBottom: '8px' }}>
          {facility.name}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280' }}>
          <MapPin size={16} />
          <span>{facility.address}</span>
        </div>
      </div>
    </div>
  );
}

function SelectUnitStep({ facility, units, loading, selectedType, setSelectedType, onUnitSelect }) {
  return (
    <>
      {/* Storage Type Filter */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Package size={24} color={facility.brand_color} />
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
            What type of storage do you need?
          </h2>
        </div>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            border: `2px solid ${facility.brand_color}`,
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          {STORAGE_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        {selectedType && (
          <p style={{ marginTop: '12px', fontSize: '14px', color: '#6B7280' }}>
            Showing {units.length} available {selectedType} unit{units.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Units Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>Loading units...</div>
      ) : units.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {units.map(unit => (
            <UnitCard key={unit.unit_id} unit={unit} facility={facility} onSelect={() => onUnitSelect(unit)} />
          ))}
        </div>
      ) : (
        <EmptyState selectedType={selectedType} onReset={() => setSelectedType('')} facility={facility} />
      )}
    </>
  );
}

function UnitCard({ unit, facility, onSelect }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: hover ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
        border: `2px solid ${hover ? facility.brand_color : '#E5E7EB'}`,
        transition: 'all 0.2s',
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        cursor: 'pointer'
      }}
      onClick={onSelect}
    >
      <div style={{ fontSize: '24px', fontWeight: '800', color: facility.brand_color, marginBottom: '12px' }}>
        Unit {unit.unit_number}
      </div>

      {unit.storage_type && (
        <div style={{
          display: 'inline-block',
          background: `${facility.brand_color}20`,
          color: facility.brand_color,
          padding: '4px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '700',
          marginBottom: '16px'
        }}>
          {unit.storage_type}
        </div>
      )}

      <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
        {unit.size_sqm}m²
      </div>

      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
        {unit.floor_level === 0 ? 'Ground Floor' : `Floor ${unit.floor_level}`}
      </div>

      {unit.features && (() => {
  // Handle different feature formats
  let featuresArray = [];
  
  if (typeof unit.features === 'string') {
    // If it's a string, split by comma
    featuresArray = unit.features.split(',').map(f => f.trim()).filter(f => f);
  } else if (Array.isArray(unit.features)) {
    // If it's already an array
    featuresArray = unit.features;
  }
  
  return featuresArray.length > 0 ? (
    <div style={{ marginBottom: '16px' }}>
      {featuresArray.slice(0, 3).map((feature, idx) => (
        <div key={idx} style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
          ✓ {feature}
        </div>
      ))}
    </div>
  ) : null;
})()}

      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '4px',
        marginBottom: '16px',
        paddingTop: '16px',
        borderTop: '2px solid #E5E7EB'
      }}>
        <DollarSign size={20} color={facility.brand_color} />
        <span style={{ fontSize: '28px', fontWeight: '800' }}>{unit.price_monthly}</span>
        <span style={{ fontSize: '14px', color: '#6B7280' }}>/month</span>
      </div>

      <button style={{
        width: '100%',
        padding: '14px',
        background: facility.brand_color,
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer'
      }}>
        Book Now
      </button>
    </div>
  );
}

function EmptyState({ selectedType, onReset, facility }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center' }}>
      <Package size={48} color="#D1D5DB" style={{ margin: '0 auto 16px' }} />
      <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>No units available</h3>
      <p style={{ color: '#6B7280' }}>
        {selectedType
          ? `No ${selectedType} units currently available. Try selecting a different storage type.`
          : 'No units currently available at this facility.'}
      </p>
      {selectedType && (
        <button
          onClick={onReset}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            background: facility.brand_color,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          View All Units
        </button>
      )}
    </div>
  );
}

function EnterDetailsStep({ 
  facility, 
  selectedUnit, 
  onBack, 
  onSubmit, 
  formErrors, 
  setFormErrors,
  bookingData,
  setBookingData,
  googleMapsLoaded 
}) {
  const [localData, setLocalData] = useState({
    customer_name: bookingData.customer_name || '',
    customer_email: bookingData.customer_email || '',
    customer_phone: bookingData.customer_phone || '',
    customer_address: bookingData.customer_address || '',
    move_in_date: bookingData.move_in_date || '',
    duration_months: bookingData.duration_months || '',
    additional_info: bookingData.additional_info || ''
  });

  // ✅ ADD: Blocked dates state
  const [blockedConfig, setBlockedConfig] = useState({
    one_off_dates: [],
    recurring_rules: []
  });

  // ✅ ADD: Load blocked dates on mount
  useEffect(() => {
    loadBlockedDates();
  }, []);

  // ✅ ADD: Function to load blocked dates
  const loadBlockedDates = async () => {
    try {
      const subdomain = window.location.hostname.split('.')[0];
      const response = await fetch(`${API_URL}/api/public/platform/facility/${subdomain}/blocked-dates`);
      const data = await response.json();
      
      console.log('📅 Blocked dates config loaded:', data);
      
      if (data.success && data.blocked_dates) {
        setBlockedConfig({
          one_off_dates: data.blocked_dates.one_off_dates || [],
          recurring_rules: data.blocked_dates.recurring_rules || []
        });
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  // ✅ ADD: Function to check if date is blocked
  const isDateBlocked = (dateString) => {
    if (!dateString) return false;
    
    // Check one-off dates
    if (blockedConfig.one_off_dates.includes(dateString)) {
      return true;
    }
    
    // Check recurring rules
    const dateObj = new Date(dateString + 'T00:00:00');
    const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 6 = Saturday
    const dayOfMonth = dateObj.getDate(); // 1-31
    const month = dateObj.getMonth() + 1; // 1-12
    
    for (const rule of blockedConfig.recurring_rules) {
      // Block specific day of week (e.g., every Sunday)
      if (rule.type === 'day_of_week' && rule.day === dayOfWeek) {
        return true;
      }
      
      // Block specific day of month (e.g., 1st of every month)
      if (rule.type === 'day_of_month' && rule.day === dayOfMonth) {
        return true;
      }
      
      // Block specific month-day combination (e.g., every Dec 25)
      if (rule.type === 'month_day' && rule.month === month && rule.day === dayOfMonth) {
        return true;
      }
    }
    
    return false;
  };

  // ✅ Initialize Google Maps Autocomplete
  useEffect(() => {
    if (!googleMapsLoaded || !window.google) return;

    const addressInput = document.getElementById('customer_address');
    
    if (addressInput) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInput, {
        componentRestrictions: { country: 'AU' },
        fields: ['formatted_address', 'address_components']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          setLocalData(prev => ({
            ...prev,
            customer_address: place.formatted_address
          }));
          setBookingData(prev => ({
            ...prev,
            customer_address: place.formatted_address
          }));
          // Clear error if exists
          if (formErrors.customer_address) {
            setFormErrors(prev => ({ ...prev, customer_address: null }));
          }
        }
      });
    }
  }, [googleMapsLoaded]);

  const handleInputChange = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
    setBookingData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user types
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // ✅ UPDATED: Custom handler for date changes
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    
    if (isDateBlocked(selectedDate)) {
      alert('⚠️ This date is not available for move-in. Please select another date.');
      return;
    }
    
    handleInputChange('move_in_date', selectedDate);
  };

  const handlePhoneChange = (value) => {
    // Only allow digits, max 10
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      handleInputChange('customer_phone', digitsOnly);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(localData);
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '32px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
        Your Details
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Full Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.customer_name ? '#EF4444' : '#374151'
          }}>
            Full Name *
          </label>
          <input
            type="text"
            name="customer_name"
            value={localData.customer_name}
            onChange={(e) => handleInputChange('customer_name', e.target.value)}
            required
            className={formErrors.customer_name ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.customer_name ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            placeholder="John Smith"
          />
          {formErrors.customer_name && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.customer_name}
            </p>
          )}
        </div>

        {/* Email */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.customer_email ? '#EF4444' : '#374151'
          }}>
            Email Address *
          </label>
          <input
            type="email"
            name="customer_email"
            value={localData.customer_email}
            onChange={(e) => handleInputChange('customer_email', e.target.value)}
            required
            className={formErrors.customer_email ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.customer_email ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            placeholder="john.smith@example.com"
          />
          {formErrors.customer_email && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.customer_email}
            </p>
          )}
        </div>

        {/* Phone */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.customer_phone ? '#EF4444' : '#374151'
          }}>
            Phone Number *
          </label>
          <input
            type="tel"
            name="customer_phone"
            value={localData.customer_phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            required
            className={formErrors.customer_phone ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.customer_phone ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            placeholder="0412345678"
            maxLength="10"
          />
          {formErrors.customer_phone && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.customer_phone}
            </p>
          )}
          <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
            10 digits, no spaces
          </p>
        </div>

        {/* Address with Google Autocomplete */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.customer_address ? '#EF4444' : '#374151'
          }}>
            Address *
          </label>
          <input
            type="text"
            id="customer_address"
            name="customer_address"
            value={localData.customer_address}
            onChange={(e) => handleInputChange('customer_address', e.target.value)}
            required
            className={formErrors.customer_address ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.customer_address ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            placeholder={googleMapsLoaded ? "Start typing your address..." : "123 Main Street, Melbourne VIC 3000"}
          />
          {formErrors.customer_address && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.customer_address}
            </p>
          )}
          {googleMapsLoaded && (
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              📍 Autocomplete enabled - start typing to see suggestions
            </p>
          )}
        </div>

        {/* Move-in Date - ✅ UPDATED */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.move_in_date ? '#EF4444' : '#374151'
          }}>
            Move-in Date *
          </label>
          <input
            type="date"
            name="move_in_date"
            value={localData.move_in_date}
            onChange={handleDateChange}  
            required
            min={new Date().toISOString().split('T')[0]}
            className={formErrors.move_in_date ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.move_in_date ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
          />
          {formErrors.move_in_date && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.move_in_date}
            </p>
          )}
          {/* ✅ ADD: Info message about blocked dates */}
          {(blockedConfig.one_off_dates.length > 0 || blockedConfig.recurring_rules.length > 0) && (
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              ℹ️ Some dates may be unavailable (holidays, maintenance, etc.)
            </p>
          )}
        </div>

        {/* Rental Duration */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: formErrors.duration_months ? '#EF4444' : '#374151'
          }}>
            Rental Duration *
          </label>
          <select
            name="duration_months"
            value={localData.duration_months}
            onChange={(e) => handleInputChange('duration_months', e.target.value)}
            required
            className={formErrors.duration_months ? 'error-field' : ''}
            style={{
              width: '100%',
              padding: '12px',
              border: `2px solid ${formErrors.duration_months ? '#EF4444' : '#E5E7EB'}`,
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
          >
            <option value="">Select duration...</option>
            <option value="1">1 Month</option>
            <option value="2">2 Months</option>
            <option value="3">3 Months</option>
            <option value="4">4 Months</option>
            <option value="5">5 Months</option>
            <option value="6">6 Months</option>
            <option value="7">7 Months</option>
            <option value="8">8 Months</option>
            <option value="9">9 Months</option>
            <option value="10">10 Months</option>
            <option value="11">11 Months</option>
            <option value="12">12 Months</option>
          </select>
          {formErrors.duration_months && (
            <p style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
              {formErrors.duration_months}
            </p>
          )}
        </div>

        {/* Additional Info */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px',
            color: '#374151'
          }}>
            Additional Information (Optional)
          </label>
          <textarea
            name="additional_info"
            value={localData.additional_info}
            onChange={(e) => handleInputChange('additional_info', e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            placeholder="Any special requirements or questions?"
          />
        </div>

        {/* Selected Unit Summary */}
        {selectedUnit && (
          <div style={{
            background: '#F3F4F6',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: '#374151' }}>
              Selected Unit
            </h4>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '4px 0' }}>
              <strong>Unit:</strong> {selectedUnit.unit_number}
            </p>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '4px 0' }}>
              <strong>Size:</strong> {selectedUnit.size_sqm}m²
            </p>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '4px 0' }}>
              <strong>Monthly Rate:</strong> ${selectedUnit.price_monthly}
            </p>
            {localData.duration_months && (
              <p style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginTop: '12px' }}>
                <strong>Total:</strong> ${(selectedUnit.price_monthly * localData.duration_months).toFixed(2)}
              </p>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: '14px 24px',
              background: 'white',
              color: '#6B7280',
              border: '2px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#F9FAFB'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            ← Back
          </button>
          <button
            type="submit"
            style={{
              flex: 1,
              padding: '14px',
              background: '#00BCD4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#0097A7'}
            onMouseOut={(e) => e.target.style.background = '#00BCD4'}
          >
            Continue to Terms →
          </button>
        </div>
      </form>
    </div>
  );
}

function FormInput({ label, ...props }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
        {label}
      </label>
      <input
        {...props}
        style={{
          width: '100%',
          padding: '12px',
          border: '2px solid #E5E7EB',
          borderRadius: '8px',
          fontSize: '14px'
        }}
      />
    </div>
  );
}

function TermsAcceptanceStep({ 
  facility, 
  selectedUnit,
  bookingData,
  termsLinks, 
  termsAccepted,
  setTermsAccepted,
  deposits,  
  depositAcceptances,  
  setDepositAcceptances,
  idVerificationRequired,  // ✅ ADD THIS PROP
  loading,
  onBack, 
  onAccept 
}) {
  const [showError, setShowError] = useState(false);

  const handleDepositToggle = (index) => {
    setDepositAcceptances(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
    setShowError(false);
  };

  const handleContinue = () => {
    if (!termsAccepted) {
      setShowError(true);
      alert('Please accept the terms and conditions to continue');
      return;
    }

    // Check all deposits are accepted
    const allDepositsAccepted = deposits.length === 0 || 
      deposits.every((_, idx) => depositAcceptances[idx]);

    if (!allDepositsAccepted) {
      setShowError(true);
      alert('Please accept all deposit requirements to continue');
      return;
    }

    setShowError(false);
    onAccept();
  };

  // ✅ UPDATED: Calculate totals including deposits AND ID verification
  const rentalTotal = selectedUnit && bookingData ? 
    selectedUnit.price_monthly * (bookingData.duration_months || 1) : 0;
  
  const depositsTotal = deposits.reduce((sum, d) => {
    if (d.is_monthly_rate && selectedUnit) {
      return sum + parseFloat(selectedUnit.price_monthly);
    }
    return sum + parseFloat(d.amount || 0);
  }, 0);
  
  const idVerificationFee = idVerificationRequired ? 3.00 : 0;  // ✅ ADD THIS
  const grandTotal = rentalTotal + depositsTotal + idVerificationFee;  // ✅ UPDATED

  const allDepositsAccepted = deposits.length === 0 || 
    deposits.every((_, idx) => depositAcceptances[idx]);

  const canContinue = termsAccepted && allDepositsAccepted;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '8px' }}>Review & Accept</h2>
        <p style={{ fontSize: '14px', color: '#6B7280' }}>Please review all terms and deposit requirements</p>
      </div>

      {showError && (
        <div style={{ background: '#FEE2E2', border: '2px solid #EF4444', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <span style={{ color: '#991B1B', fontWeight: '600' }}>
            {!termsAccepted ? 'You must accept the terms to continue' : 'You must accept all deposit requirements to continue'}
          </span>
        </div>
      )}

      {/* ✅ ADD: ID VERIFICATION INFO BOX */}
      {idVerificationRequired && (
        <div style={{
          background: '#DBEAFE',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: '#1E40AF', 
            marginBottom: '12px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <span style={{ fontSize: '24px' }}>🆔</span>
            ID Verification Required
          </div>
          <p style={{ 
            fontSize: '14px', 
            color: '#1E3A8A', 
            margin: 0, 
            lineHeight: '1.6' 
          }}>
            This facility requires identity verification for all bookings. After payment, you'll be asked to take a photo of your ID and a quick selfie. Verification takes about 30 seconds and is powered by Stripe for maximum security. The $3.00 verification fee is included in your total below.
          </p>
        </div>
      )}

      {selectedUnit && bookingData && (
        <div style={{ background: 'white', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>Order Summary</h3>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Customer:</strong> {bookingData.customer_name}</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Email:</strong> {bookingData.customer_email}</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Unit:</strong> {selectedUnit.unit_number}</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Size:</strong> {selectedUnit.size_sqm}m²</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Monthly Rate:</strong> ${selectedUnit.price_monthly}</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}><strong>Duration:</strong> {bookingData.duration_months} month(s)</div>
          </div>

          <div style={{ borderTop: '2px solid #E5E7EB', paddingTop: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
              <span>Rental Total</span>
              <span>${rentalTotal.toFixed(2)}</span>
            </div>

            {deposits.length > 0 && (
              <>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginTop: '12px', marginBottom: '8px' }}>
                  Deposits (Refundable):
                </div>
                {deposits.map((deposit, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                    <span>{deposit.type}</span>
                    <span>
                      {deposit.is_monthly_rate ? (
                        `$${selectedUnit.price_monthly.toFixed(2)} (= Monthly Rate)`
                      ) : (
                        `$${parseFloat(deposit.amount).toFixed(2)}`
                      )}
                    </span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#F59E0B', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #E5E7EB' }}>
                  <span>Deposits Subtotal</span>
                  <span>${depositsTotal.toFixed(2)}</span>
                </div>
              </>
            )}

            {/* ✅ ADD: ID VERIFICATION FEE LINE ITEM */}
            {idVerificationRequired && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '14px', 
                color: '#6B7280', 
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>ID Verification (one-time)</span>
                  <span style={{ 
                    fontSize: '11px', 
                    background: '#DBEAFE', 
                    color: '#1E40AF', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    Required
                  </span>
                </div>
                <span style={{ fontWeight: '600' }}>${idVerificationFee.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* ✅ UPDATED: Grand total now includes ID verification */}
          <div style={{ borderTop: '2px solid #E5E7EB', paddingTop: '16px', fontSize: '20px', fontWeight: '700', color: facility.brand_color || '#00BCD4', display: 'flex', justifyContent: 'space-between' }}>
            <span>Total Due Today</span>
            <span>${grandTotal.toFixed(2)}</span>
          </div>
        </div>
      )}

      {deposits.length > 0 && (
        <div style={{ background: '#FFF7ED', border: '2px solid #F59E0B', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#92400E' }}>
            Deposit Requirements
          </h3>
          <p style={{ fontSize: '14px', color: '#92400E', marginBottom: '16px' }}>
            The following refundable deposits are required. Please review and accept each one:
          </p>

          {deposits.map((deposit, idx) => (
            <label key={idx} style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              padding: '16px', 
              background: 'white', 
              border: depositAcceptances[idx] ? '2px solid #10B981' : '2px solid #E5E7EB', 
              borderRadius: '12px', 
              marginBottom: '12px', 
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              <input 
                type="checkbox" 
                checked={depositAcceptances[idx] || false} 
                onChange={() => handleDepositToggle(idx)} 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  cursor: 'pointer', 
                  marginRight: '12px',
                  marginTop: '2px'
                }} 
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '4px' }}>
                  {deposit.type}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#F59E0B', marginBottom: '4px' }}>
                  {deposit.is_monthly_rate ? (
                    <>
                      ${selectedUnit ? selectedUnit.price_monthly.toFixed(2) : '0.00'}
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginLeft: '8px' }}>
                        (= Monthly Rate)
                      </span>
                    </>
                  ) : (
                    `$${parseFloat(deposit.amount).toFixed(2)}`
                  )}
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  {deposit.is_monthly_rate 
                    ? 'Deposit equals one month of rent - refundable upon return in good condition'
                    : 'Refundable upon return of unit in good condition'
                  }
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {/* TERMS SECTION */}
      {termsLinks && termsLinks.length > 0 ? (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px' }}>Terms & Conditions</h3>
          {termsLinks.map((link, index) => (
            <div key={index} style={{ background: '#F9FAFB', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px', color: '#1F2937' }}>
                {link.name || link.title || 'Terms & Conditions'}
              </div>
              <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: facility.brand_color || '#00BCD4', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                View Document →
              </a>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: '#F9FAFB', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'center', color: '#6B7280' }}>
          By proceeding, you agree to the facility's terms and conditions.
        </div>
      )}

      {/* TERMS ACCEPTANCE CHECKBOX */}
      <div style={{ background: termsAccepted ? '#D1FAE5' : '#FEF3C7', border: `2px solid ${termsAccepted ? '#10B981' : '#F59E0B'}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <label style={{ display: 'flex', alignItems: 'start', gap: '12px', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={termsAccepted} 
            onChange={(e) => { 
              setTermsAccepted(e.target.checked); 
              setShowError(false); 
            }} 
            style={{ 
              width: '20px', 
              height: '20px', 
              cursor: 'pointer', 
              marginTop: '2px' 
            }} 
          />
          <span style={{ fontSize: '16px', fontWeight: '600', color: termsAccepted ? '#065F46' : '#92400E' }}>
            I have read and accept all terms and conditions
          </span>
        </label>
      </div>

      {/* BUTTONS */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          onClick={onBack} 
          disabled={loading} 
          style={{ 
            flex: 1, 
            padding: '16px', 
            background: 'white', 
            color: '#6B7280', 
            border: '2px solid #E5E7EB', 
            borderRadius: '12px', 
            fontSize: '16px', 
            fontWeight: '700', 
            cursor: loading ? 'not-allowed' : 'pointer', 
            opacity: loading ? 0.5 : 1 
          }}
        >
          Back
        </button>
        <button 
          onClick={handleContinue} 
          disabled={!canContinue || loading} 
          style={{ 
            flex: 2, 
            padding: '16px', 
            background: (!canContinue || loading) ? '#9CA3AF' : (facility.brand_color || '#00BCD4'), 
            color: 'white', 
            border: 'none', 
            borderRadius: '12px', 
            fontSize: '16px', 
            fontWeight: '700', 
            cursor: (!canContinue || loading) ? 'not-allowed' : 'pointer' 
          }}
        >
          {loading ? 'Processing...' : 'Continue to Payment'}
        </button>
      </div>

      {/* WARNING MESSAGE */}
      {!canContinue && (
        <p style={{ textAlign: 'center', fontSize: '14px', color: '#F59E0B', marginTop: '16px', fontWeight: '600' }}>
          {!termsAccepted && 'Please accept the terms and conditions'}
          {termsAccepted && !allDepositsAccepted && 'Please accept all deposit requirements'}
        </p>
      )}

    </div>
  );
}


export default App;
